import InputField from 'components/common/InputField';
import React, { useEffect, useRef, useState } from 'react';
import styles from 'styles/components/SignUpForm.module.sass';
import zeroneLogoDark from 'assets/images/svg/logo-dark.svg'
import { Link, useNavigate, useLocation } from 'react-router-dom';
import SecondaryButton from 'components/common/SecondaryButton';
import PrimaryButton from 'components/common/PrimaryButton';
import CountryPicker from 'components/common/CountryPicker';
import CountryValue from 'interface/CountryValue';
import { validateAndSanitizeCreateAccount } from 'utils/ValidateCreateAccount';
import { SignUpField, SignUpFieldErrors, validateSignUpFields } from 'utils/SignUpRules';
import getDefaultCountryFromLocale from 'utils/DefaultCountryFromLocale';
import countryDetails from 'constants/data/global/CountryData';
import { useToast } from 'provider/ToastProvider';

/**
 * SignUpForm component renders a user registration form with fields for personal details,
 * and action buttons for signing up or logging in.
 *
 * @component
 * @example
 * Example usage:
 * 
 * ```tsx
 * import SignUpForm from 'components/SignUpForm';
 * 
 * const App: React.FC = () => (
 *   <div>
 *     <SignUpForm />
 *   </div>
 * );
 *
 * export default App;
 * ```
 *
 * @returns {JSX.Element} The JSX element displaying the sign-up form.
 */
const SignUpForm: React.FC = () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const { addToast } = useToast()
    // One message per invalid field, shown right under the field. Step 1 cannot be left while any is set.
    const [fieldErrors, setFieldErrors] = useState<SignUpFieldErrors>({})
    const [attempted, setAttempted] = useState<boolean>(false)
    const [focusTick, setFocusTick] = useState<number>(0)
    const formRef = useRef<HTMLFormElement>(null)
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [gender, setGender] = useState<string>('');
    const [country, setCountry] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [countryCode, setCountryCode] = useState<string>('')
    const [companyName, setCompanyName] = useState<string>('')
    const [jobTitle, setJobTitle] = useState<string>('')

    const navigate = useNavigate();
    const location = useLocation();

    // Detect invite flow: /sign-up?invited=1[&email=xxx]
    // A later step can send the person back here with what they typed (never the passwords) and what to fix.
    const returned = (location.state || {}) as { prefill?: Partial<Record<'firstName' | 'lastName' | 'email' | 'gender' | 'country' | 'countryCode' | 'phone' | 'companyName' | 'jobTitle', string>>; fieldErrors?: SignUpFieldErrors }

    const searchParams = new URLSearchParams(location.search);
    const isInvited = searchParams.get('invited') === '1';
    const invitedEmail = searchParams.get('email') ?? '';

    useEffect(() => {
        if (invitedEmail) setEmail(decodeURIComponent(invitedEmail));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invitedEmail]);

    useEffect(() => {
        const defaultCountryISO = getDefaultCountryFromLocale()
        const defaultCountry = countryDetails[defaultCountryISO]

        if (defaultCountry) {
            setCountry(defaultCountry.name || '')
            setCountryCode(defaultCountry.iso || '')
            setPhone(defaultCountry.code || '')
        }
    }, [])

    useEffect(() => {
        const p = returned.prefill
        if (p) {
            setFirstName(p.firstName ?? '')
            setLastName(p.lastName ?? '')
            setEmail(p.email ?? '')
            setGender(p.gender ?? '')
            setCountry(p.country ?? '')
            setCountryCode(p.countryCode ?? '')
            setPhone(p.phone ?? '')
            setCompanyName(p.companyName ?? '')
            setJobTitle(p.jobTitle ?? '')
        }
        if (returned.fieldErrors && Object.keys(returned.fieldErrors).length > 0) {
            setFocusTick((n) => n + 1)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // After the first failed attempt, keep the messages in step with what is typed, so they clear as soon as a field is fixed.
    useEffect(() => {
        if (!attempted) return
        setFieldErrors(validateSignUpFields({ firstName, lastName, email, gender, country, countryCode, phone, password, confirmPassword, companyName, jobTitle }))
    }, [attempted, firstName, lastName, email, gender, country, countryCode, phone, password, confirmPassword, companyName, jobTitle])

    // Move focus to the first invalid field after a blocked attempt.
    useEffect(() => {
        if (focusTick === 0) return
        const first = formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"], [data-invalid="true"]')
        first?.scrollIntoView?.({ block: 'center' })
        first?.focus?.()
    }, [focusTick])

    // Messages handed back by a later step stay visible until the person edits that field. The live check below
    // re-derives its own messages from what is typed, which would otherwise wipe them straight away.
    const handedBack: SignUpFieldErrors = returned.fieldErrors ?? {}
    const handedBackFrom: Partial<Record<SignUpField, string>> = {
        firstName: returned.prefill?.firstName ?? '', lastName: returned.prefill?.lastName ?? '', email: returned.prefill?.email ?? '',
        gender: returned.prefill?.gender ?? '', country: returned.prefill?.country ?? '', phone: returned.prefill?.phone ?? '',
        companyName: returned.prefill?.companyName ?? '', jobTitle: returned.prefill?.jobTitle ?? '', password: '', confirmPassword: '',
    }
    const currentValue: Record<SignUpField, string> = { firstName, lastName, email, gender, country, phone, companyName, jobTitle, password, confirmPassword }
    const shownError = (field: SignUpField): string | undefined =>
        fieldErrors[field] ?? (handedBack[field] && currentValue[field] === (handedBackFrom[field] ?? '') ? handedBack[field] : undefined)
    const hasShownError = (Object.keys(currentValue) as SignUpField[]).some((field) => Boolean(shownError(field)))

    function handleLoginClicked() {
        navigate('/login')
    }

    function handleNextClicked(event: React.FormEvent) {
        event.preventDefault()

        const {
            isValid,
            sanitizedFirstName,
            sanitizedLastName,
            sanitizedEmail,
            sanitizedGender,
            sanitizedCountry,
            sanitizedCountryCode,
            sanitizedPhone,
            sanitizedPassword,
            sanitizedConfirmPassword,
            sanitizedCompanyName,
            sanitizedJobTitle,
            fieldErrors: foundErrors
        } = validateAndSanitizeCreateAccount({
            firstName,
            lastName,
            email,
            gender,
            country,
            countryCode,
            phone,
            password,
            confirmPassword,
            companyName,
            jobTitle,
        })

        if (isValid) {
            const baseState = {
                firstName: sanitizedFirstName,
                lastName: sanitizedLastName,
                email: sanitizedEmail,
                gender: sanitizedGender,
                country: sanitizedCountry,
                countryCode: sanitizedCountryCode,
                phone: sanitizedPhone,
                password: sanitizedPassword,
                confirmPassword: sanitizedConfirmPassword,
                companyName: sanitizedCompanyName,
                jobTitle: sanitizedJobTitle,
            };

            if (isInvited) {
                // Skip account-type selection — go straight to free account signup
                navigate('/sign-up-payment', {
                    state: { ...baseState, accountType: 'free', billingCycle: 'monthly', isInvited: true }
                });
            } else {
                navigate('/account-type', { state: baseState });
            }
        } else {
            // Blocked: nothing moves to step 2 until every field is acceptable.
            const count = Object.keys(foundErrors).length
            setAttempted(true)
            setFieldErrors(foundErrors)
            setFocusTick((n) => n + 1)

            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            addToast({
                message: count === 1 ? 'Please fix the highlighted field before continuing.' : `Please fix the ${count} highlighted fields before continuing.`,
                type: 'error',
            })
        }
    }

    const handleCountryChange = (selectedCountry: CountryValue) => {
        requestAnimationFrame(() => {
            setCountryCode(selectedCountry.iso || '')
            setCountry(selectedCountry.name || '')
            setPhone(selectedCountry.code || '')
        });
    };

    return (
        <form className={styles['sign-up-form']} ref={formRef} noValidate>
            <div className={styles['logo-wrapper']}>
                <Link to={"/"}>
                    <img src={zeroneLogoDark} className={styles['logo']} alt='Zerone AnalytiQs Logo' />
                </Link>
            </div>
            <h2 className={styles['form-title']}>Create an Account</h2>
            <div className={styles['all-input']}>

            <div className={styles['input-row']}>
                <InputField 
                    label={'First Name'}
                    error={shownError('firstName')} 
                    type={'text'} 
                    value={firstName} 
                    onChange={setFirstName} 
                    required={true} 
                />
                <InputField 
                    label={'Last Name'}
                    error={shownError('lastName')} 
                    type={'text'} 
                    value={lastName} 
                    onChange={setLastName} 
                    required={true} 
                />
            </div>

            <div className={styles['input-row']}>
                <InputField 
                    label={'Email'}
                    error={shownError('email')} 
                    type={'email'} 
                    value={email} 
                    onChange={setEmail} 
                    required={true} 
                />
            </div>

            <div className={styles['input-container']}>
                <label className={styles['label']}>
                    Gender<span className={styles['required']}>*</span>
                </label>
                <div className={styles['radio-group']} data-invalid={shownError('gender') ? 'true' : undefined} tabIndex={-1}>
                    <label className={styles['radio-label']}>
                        <input 
                            type="radio" 
                            value="male" 
                            checked={gender === 'male'} 
                            onChange={(e) => setGender(e.target.value)} 
                            className={styles['radio-input']}
                        />
                        <span className={styles['radio-text']}>Male</span>
                    </label>
                    <label className={styles['radio-label']}>
                        <input 
                            type="radio" 
                            value="female" 
                            checked={gender === 'female'} 
                            onChange={(e) => setGender(e.target.value)} 
                            className={styles['radio-input']}
                        />
                        <span className={styles['radio-text']}>Female</span>
                    </label>
                    <label className={styles['radio-label']}>
                        <input 
                            type="radio" 
                            value="rather_not_say" 
                            checked={gender === 'rather_not_say'} 
                            onChange={(e) => setGender(e.target.value)} 
                            className={styles['radio-input']}
                        />
                        <span className={styles['radio-text']}>Rather Not Say</span>
                    </label>
                </div>
            </div>

            {shownError('gender') && <span className={styles['error']} role="alert">{shownError('gender')}</span>}

            <div className={styles['input-row']} data-invalid={shownError('country') ? 'true' : undefined} tabIndex={-1}>
                
                <CountryPicker onChange={handleCountryChange} initialIso={returned.prefill?.countryCode ? returned.prefill.countryCode.toUpperCase() : undefined} />
                <InputField 
                    label={'Phone Number'}
                    error={shownError('phone')} 
                    type={'tel'} 
                    value={phone} 
                    onChange={setPhone} 
                    required={true} 
                />
            </div>
            {shownError('country') && <span className={styles['error']} role="alert">{shownError('country')}</span>}
            

            <div className={styles['input-row']}>
                <InputField 
                    label={'Company Name'}
                    error={shownError('companyName')} 
                    type={'text'} 
                    value={companyName} 
                    onChange={setCompanyName} 
                    required={false} 
                />
                <InputField 
                    label={'Job Title'}
                    error={shownError('jobTitle')} 
                    type={'text'} 
                    value={jobTitle} 
                    onChange={setJobTitle} 
                    required={false} 
                />
            </div>

            <div className={styles['input-row']}>
                <InputField
                    label={'Create Password'}
                    error={shownError('password')}
                    type={'password'}
                    value={password}
                    onChange={setPassword}
                    required={true}
                />
                <InputField
                    label={'Confirm Password'}
                    error={shownError('confirmPassword')}
                    type={'password'}
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    required={true}
                />
            </div>
            </div>
        
            <p className={styles['privacy-note']}>
                By registering, you confirm that you agree to the processing of your personal data by Zerone Analytiqs as described in the{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className={styles['privacy-link']}>Privacy Statement</a>.
            </p>
            <div className={styles['action-buttons']}>
                <PrimaryButton
                    text={isInvited ? 'Create Free Account' : 'Next'}
                    primaryColor='#FF5522'
                    textColor='#FFFFFF'
                    hoverColor='#FFFFFF'
                    hoverTextColor='#FF5522'
                    doSomething={(event: React.FormEvent) => handleNextClicked(event)}
                />

                <SecondaryButton
                    text="Have an account? Login"
                    primaryColor="#FFFFFF"
                    textColor="#FF5522"
                    hoverColor="#FF5522"
                    hoverTextColor="#FFFFFF"
                    doSomething={() => handleLoginClicked()}
                />
            </div>          
            {hasShownError && (
                <span className={styles['error']} role="alert">
                    Some details need your attention. Fix the highlighted fields above to continue.
                </span>
            )}
        </form>
    );
};

export default SignUpForm;